/**
 * The help link of the loader's public surface: <a href="…/de/articles/so-gehts" data-leadwin-article>.
 *
 * The link's own address is the key. It already names the article (by its slug, which is unique per help
 * center and language) and the language it is meant in, so a second copy of that in the attribute would
 * only be a second place to drift from the first. The attribute is the marker that says "open this in the
 * agent"; a value in it still wins, for a page that wants to name the article by its id instead.
 */

/* Mirror of WidgetHelpCenterPageSegments.Articles (Feedbax.Domain): the segment an article page lives
 * under on every help-center address, a provider's own hostname included. */
const articlesSegment = 'articles';

/* A content language as the addresses spell it. Anything else before the segment is a provider segment
 * or a base path, and LeadWin's own English pages carry no language at all. */
const languageSegment = /^[a-z]{2}$/;

/* What may overrule the address: an article id (a GUID, with or without dashes) or a slug with its
 * language. Anything else in the attribute is the marker alone. JSX writes a bare attribute as "true",
 * other templates write "", "1" or the attribute's own name, and none of that names an article. */
const explicitKey = /^(?:[0-9a-f]{8}-?(?:[0-9a-f]{4}-?){3}[0-9a-f]{12}|[a-z]{2}\/[\w-]+)$/i;

/**
 * The article key a help link names: the attribute's own value where it is an article key, otherwise the
 * language and slug of its address as "de/so-gehts", or the bare slug where the address names no
 * language. Empty when the link carries neither, so the caller lets it navigate.
 * @param {HTMLElement} link - Element carrying data-leadwin-article
 * @returns {string}
 */
export function readArticleLinkKey(link) {
    const explicit = (link.dataset.leadwinArticle || '').trim();

    if (explicitKey.test(explicit)) {
        return explicit;
    }

    const segments = pathSegments(link.getAttribute('href'));
    const at = segments.lastIndexOf(articlesSegment);

    if (at < 0 || at === segments.length - 1) {
        return '';
    }

    const slug = segments[at + 1];
    const language = at > 0 ? segments[at - 1].toLowerCase() : '';

    return languageSegment.test(language) ? language + '/' + slug : slug;
}

/**
 * The decoded path segments of an address, relative ones resolved against the page. Empty for an address
 * that does not parse, or one whose escapes are broken.
 * @param {string|null} href - The link's href attribute
 * @returns {string[]}
 */
function pathSegments(href) {
    if (!href) {
        return [];
    }

    try {
        return new URL(href, document.baseURI).pathname
            .split('/')
            .filter(Boolean)
            .map(decodeURIComponent);
    } catch {
        return [];
    }
}

/* The targets that keep the navigation in this window or one of its own ancestors. Anything else, _blank
 * or a named window, asks the browser for a new one. */
const sameWindowTargets = ['', '_self', '_top', '_parent'];

/**
 * Whether a help link opens its page in a new tab or window, by its own target or the document's base.
 * Such a link is left to the browser: the widget only learns late that it cannot show an article, and by
 * then the click that could open a new tab is spent, so the popup blocker would swallow the fallback.
 * @param {HTMLElement} link - Element carrying data-leadwin-article
 * @returns {boolean}
 */
export function opensNewTab(link) {
    // Only a link or an image-map area navigates at all, so only they read a target, the base's included.
    // A button carrying the attribute stays the JS API without script.
    const tag = link.tagName.toUpperCase();

    if (tag !== 'A' && tag !== 'AREA') {
        return false;
    }

    const base = link.ownerDocument.querySelector('base[target]');
    const target = link.getAttribute('target') ?? (base ? base.getAttribute('target') : '') ?? '';

    return !sameWindowTargets.includes(target.trim().toLowerCase());
}
